import { useEffect } from 'react';
import { useSpriteArt, warmPoseSheets } from './emjaeSprite';
import {
  EMJAE_MASCOT_SIZES,
  SPRITE_PLACEHOLDER,
  SPRITE_SHEET,
  emjaeMascotClass,
  type EmjaeMascotMotion,
  type EmjaeMascotPose,
  type EmjaeMascotSize,
} from './emjaeMascotMotion';

interface EmjaeMascotProps {
  size: EmjaeMascotSize;
  motion?: readonly (EmjaeMascotMotion | false | null | undefined)[];
  /** Which art slot to show. The launcher, header and hero play it as a sprite; avatars stay still. */
  pose?: EmjaeMascotPose;
  /** Avatars only: this is the avatar beside the reply being generated, so it plays the 64 sprite for `pose`
   *  (thinking / talking); any other avatar is the still image. */
  live?: boolean;
  /** Overrides the width/height attribute (the launcher is 52 on narrow screens). */
  px?: number;
  /** Extra classes after the size box, to render a size slightly off its default (the top-bar badger is 36px). */
  className?: string;
  /** Clears a one-shot (bounce / wiggle) when its animation ends, so the next
   *  trigger can play again and a remount does not replay a finished one. */
  onAnimationEnd?: () => void;
  /** One full cycle of an infinite sprite finished (the idle cycle counts these). */
  onAnimationIteration?: () => void;
}

export function EmjaeMascot({ size, motion = [], pose = 'idle', live = false, px: pxOverride, className, onAnimationEnd, onAnimationIteration }: EmjaeMascotProps) {
  const { px: defaultPx, src } = EMJAE_MASCOT_SIZES[size];
  const px = pxOverride ?? defaultPx;
  const sheet = SPRITE_SHEET[size] ?? (size === 'avatar' && live ? '64' : undefined);
  // The sprite is the element's background. If its sheet fails, the still image takes over
  // at the same size (width and height never depend on loading).
  // The art shown can lag the pose while a sheet arrives; data-pose is always the pose.
  const { usable, shownPose } = useSpriteArt(sheet, live || size !== 'avatar' ? pose : 'idle');
  // A live avatar shows the still image until its first sheet is in (the hook starts on idle art, which an avatar never plays).
  const spriteOn = usable && (size !== 'avatar' || shownPose !== 'idle');
  useEffect(() => {
    if (sheet) warmPoseSheets();
  }, [sheet]);
  return (
    <img
      src={spriteOn ? SPRITE_PLACEHOLDER : src}
      width={px}
      height={px}
      draggable={false}
      data-testid="emjae-mascot"
      data-size={size}
      data-pose={size === 'avatar' && !live ? 'idle' : pose}
      // The hero is the one picture on the page; the others sit beside the
      // word "Emjae" or a message and are decoration.
      alt={size === 'hero' ? 'Emjae' : ''}
      aria-hidden={size === 'hero' ? undefined : true}
      className={emjaeMascotClass(size, motion, spriteOn ? { pose: shownPose, sheet } : undefined, className)}
      onAnimationEnd={onAnimationEnd}
      onAnimationIteration={onAnimationIteration}
    />
  );
}

